/**
 * SWEPass.ts — stylised shallow-water seiches simulation.
 *
 * Holds two ping-pong rgba32float textures (state A & B). Each frame runs a
 * single integration step, then swaps. The "current" state texture (i.e. the
 * one most recently written) is exposed via `etaView` for the water shader
 * which adds eta to the displaced surface height.
 *
 * Re-uploads uniforms whenever wind, water level, or dt changes.
 */

import sweWgsl from "../shaders/swe.wgsl?raw";
import type { TerrainRenderPass } from "./TerrainRenderPass";
import { createUniformBuffer } from "../gpu/Buffers";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";

const STRUCT_SIZE = 48; // 12 × f32

export interface SWEPassOptions {
  /** Grid resolution. Default 512. */
  resolution?: number;
  /** Effective gravity (m/s^2). Tuned for ~2-4 min basin seiche periods. */
  gEff?: number;
  /** Linear damping (1/s). Default 0.02 (~50 s e-fold). */
  gamma?: number;
  /** Wind drive coefficient (acceleration per (m/s)² in normalised space). */
  driveK?: number;
}

export class SWEPass implements RenderPass {
  readonly name = "SWEPass";

  readonly resolution: number;
  readonly gEff: number;
  readonly gamma: number;
  readonly driveK: number;
  readonly extent: number;

  private device: GPUDevice;
  private terrainPass: TerrainRenderPass;
  private uniform: GPUBuffer;
  private pipeline: GPUComputePipeline;
  private bgl: GPUBindGroupLayout;
  private texA: GPUTexture;
  private texB: GPUTexture;
  private viewA: GPUTextureView;
  private viewB: GPUTextureView;
  private bgAB: GPUBindGroup; // reads A, writes B
  private bgBA: GPUBindGroup; // reads B, writes A
  /** True iff the most-recently-written texture is B. */
  private currentIsB = true;

  constructor(
    device: GPUDevice,
    terrainPass: TerrainRenderPass,
    opts: SWEPassOptions = {},
  ) {
    this.device = device;
    this.terrainPass = terrainPass;
    this.resolution = opts.resolution ?? 512;
    // 200 made basin modes too fast (tens of seconds). Keep c = sqrt(gEff*depth)
    // in a realistic inland-lake range so dominant periods land near minutes.
    this.gEff = opts.gEff ?? 14;
    this.gamma = opts.gamma ?? 0.02;
    this.driveK = opts.driveK ?? 0.0012;
    this.extent = terrainPass.heightmap.extent;

    const makeTex = (label: string) =>
      device.createTexture({
        label,
        size: { width: this.resolution, height: this.resolution },
        format: "rgba32float",
        usage:
          GPUTextureUsage.STORAGE_BINDING |
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.COPY_DST,
      });
    this.texA = makeTex("SWEPass.stateA");
    this.texB = makeTex("SWEPass.stateB");
    this.viewA = this.texA.createView({ label: "SWE.A.view" });
    this.viewB = this.texB.createView({ label: "SWE.B.view" });

    // Zero-initialise both textures so the first read is well-defined.
    const zeros = new Float32Array(this.resolution * this.resolution * 4);
    const layout = {
      bytesPerRow: this.resolution * 16,
      rowsPerImage: this.resolution,
    };
    const size = { width: this.resolution, height: this.resolution };
    device.queue.writeTexture({ texture: this.texA }, zeros, layout, size);
    device.queue.writeTexture({ texture: this.texB }, zeros, layout, size);

    this.uniform = createUniformBuffer(device, STRUCT_SIZE, "SWEPass.params");

    const module = device.createShaderModule({
      label: "swe.wgsl",
      code: sweWgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "SWEPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: { access: "write-only", format: "rgba32float" },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "SWEPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      compute: { module, entryPoint: "cs_main" },
    });

    const heightView = terrainPass.heightmapView();
    this.bgAB = device.createBindGroup({
      label: "SWEPass.bg.AB",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: heightView },
        { binding: 2, resource: this.viewA },
        { binding: 3, resource: this.viewB },
      ],
    });
    this.bgBA = device.createBindGroup({
      label: "SWEPass.bg.BA",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: heightView },
        { binding: 2, resource: this.viewB },
        { binding: 3, resource: this.viewA },
      ],
    });
  }

  /** View of the most-recently-written state texture (consumed by water). */
  get etaView(): GPUTextureView {
    return this.currentIsB ? this.viewB : this.viewA;
  }

  /** Both views (for consumers that want to bind both up-front and select). */
  views(): { a: GPUTextureView; b: GPUTextureView } {
    return { a: this.viewA, b: this.viewB };
  }

  /** True iff `etaView` currently returns view B. */
  get currentIsViewB(): boolean {
    return this.currentIsB;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const { state, dt } = frame;
    // Sub-step at most 2 ticks of 1/60 s to absorb large frame gaps without
    // exploding the simulation.
    const stepDt = Math.min(0.0166, dt);
    const a = (state.windDirectionDeg * Math.PI) / 180;
    const wx = -Math.sin(a);
    const wz = Math.cos(a);
    const data = new Float32Array(STRUCT_SIZE / 4);
    data[0] = this.resolution;
    data[1] = this.extent;
    data[2] = this.terrainPass.waterLevel;
    data[3] = stepDt;
    data[4] = this.gEff;
    data[5] = this.gamma;
    data[6] = wx;
    data[7] = wz;
    data[8] = state.windSpeed;
    data[9] = this.driveK;
    this.device.queue.writeBuffer(this.uniform, 0, data);

    const pass = encoder.beginComputePass({ label: "SWEPass" });
    pass.setPipeline(this.pipeline);
    // Read FROM "current", write TO "other". Swap currentIsB after.
    pass.setBindGroup(0, this.currentIsB ? this.bgBA : this.bgAB);
    const wg = Math.ceil(this.resolution / 8);
    pass.dispatchWorkgroups(wg, wg, 1);
    pass.end();
    this.currentIsB = !this.currentIsB;
  }

  destroy(): void {
    this.texA.destroy();
    this.texB.destroy();
  }
}
